/**
 * Medicine Service
 * Handles all medicine-related API calls
 */

import axios from '../api/axios';
import { API_ENDPOINTS } from './api.config';

class MedicineService {
  /**
   * Get list of medicines
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20, max: 100)
   * @param {string} params.search - Search query (optional)
   * @param {string} params.category - Filter by category (optional)
   * @returns {Promise<Object>} Response with medicines array and pagination
   */
  async getMedicines(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 20,
      });

      if (params.search) queryParams.append('search', params.search);
      if (params.category) queryParams.append('category', params.category);

      const response = await axios.get(
        `${API_ENDPOINTS.MEDICINES.LIST}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get medicine detail by ID
   * @param {number} medicineId - Medicine ID
   * @returns {Promise<Object>} Medicine detail object
   */
  async getMedicineDetail(medicineId) {
    try {
      const response = await axios.get(
        API_ENDPOINTS.MEDICINES.DETAIL(medicineId)
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Search medicines
   * @param {string} query - Search query
   * @returns {Promise<Object>} Response with suggestions array
   */
  async searchMedicines(query) {
    try {
      const response = await axios.get(
        `${API_ENDPOINTS.MEDICINES.SEARCH}?q=${encodeURIComponent(query)}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Upload prescription image
   * @param {File} imageFile - Prescription image file
   * @returns {Promise<Object>} Response with uploaded prescription data
   */
  async uploadPrescription(imageFile) {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await axios.post(
        API_ENDPOINTS.PRESCRIPTIONS.UPLOAD,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user prescriptions
   * @returns {Promise<Object>} Response with prescriptions array
   */
  async getPrescriptions() {
    try {
      const response = await axios.get(API_ENDPOINTS.PRESCRIPTIONS.LIST);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Place medicine order
   * @param {Object} orderData - Order data
   * @param {Array<Object>} orderData.items - Array of {medicine_id, quantity} objects (required)
   * @param {number} orderData.address_id - Delivery address ID (required)
   * @param {string} orderData.ordered_for_name - Patient name (optional)
   * @param {string} orderData.ordered_for_phone - Patient phone (optional)
   * @returns {Promise<Object>} Response with order details
   */
  async placeOrder(orderData) {
    try {
      // Ensure items are in correct format: [{medicine_id, quantity}]
      const items = orderData.items || orderData.medicines?.map(m => ({
        medicine_id: m.medicine_id || m.id || m,
        quantity: m.quantity || 1
      }));

      const response = await axios.post(API_ENDPOINTS.ORDERS.PLACE, {
        items: items,
        address_id: orderData.address_id,
        ordered_for_name: orderData.ordered_for_name,
        ordered_for_phone: orderData.ordered_for_phone,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get order history
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @param {string} params.status - Filter by status (optional)
   * @returns {Promise<Object>} Response with orders array and pagination
   */
  async getOrderHistory(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 20,
      });

      if (params.status) queryParams.append('status', params.status);

      const response = await axios.get(
        `${API_ENDPOINTS.ORDERS.HISTORY}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get medicine order detail
   * @param {number} orderId - Order ID
   * @returns {Promise<Object>} Response with order details
   */
  async getOrderDetail(orderId) {
    try {
      const response = await axios.get(API_ENDPOINTS.ORDERS.DETAIL(orderId));
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Cancel medicine order
   * @param {number} orderId - Order ID
   * @param {string} reason - Cancellation reason
   * @returns {Promise<Object>} Response with cancellation confirmation
   */
  async cancelOrder(orderId, reason) {
    try {
      const response = await axios.post(API_ENDPOINTS.ORDERS.CANCEL(orderId), {
        reason: reason,
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get download invoice URL
   * @param {number} orderId - Order ID
   * @returns {string} Invoice download URL
   */
  getInvoiceUrl(orderId) {
    return `${API_ENDPOINTS.ORDERS.DOWNLOAD_INVOICE(orderId)}`;
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      const errorMessage = error.response.data.error || error.response.data.message || 'Something went wrong';
      return new Error(errorMessage);
    } else if (error.request) {
      return new Error('Network error. Please check your connection.');
    } else {
      return new Error('An error occurred. Please try again.');
    }
  }
}

export default new MedicineService();
